/**
 * Admin: Credential Sets management page.
 *
 * List all credential sets, create/edit/delete sets, manage keys within
 * sets, and assign/unassign users.
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { apiClient } from '@/services/apiClient'
import {
  Button,
  Badge,
  Alert,
  AlertDescription,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Input,
  Textarea,
  Spinner,
} from '@/components/ui'
import {
  Plus,
  Key,
  Users,
  Pencil,
  Trash2,
} from 'lucide-react'
import { ManageSetUsersModal } from './ManageSetUsersModal'

interface CredentialSetSummary {
  id: string
  name: string
  description: string | null
  entry_count: number
  services: string[]
  assigned_user_count: number
  created_by_name: string | null
  created_at: string
  updated_at: string
  origin: 'oauth' | 'manual'
  origin_adapter_id: string | null
}

export function CredentialSetsPage() {
  const [sets, setSets] = useState<CredentialSetSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create modal
  const [showCreate, setShowCreate] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createDesc, setCreateDesc] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Edit metadata modal
  const [editTarget, setEditTarget] = useState<CredentialSetSummary | null>(null)
  const [editName, setEditName] = useState('')
  const [editDesc, setEditDesc] = useState('')
  const [isSavingEdit, setIsSavingEdit] = useState(false)

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<CredentialSetSummary | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Sub-modals
  const [usersTarget, setUsersTarget] = useState<CredentialSetSummary | null>(null)

  const fetchSets = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/admin/credential-sets')
      if (!res.ok) throw new Error('Failed to fetch credential sets')
      const data = await res.json()
      setSets(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credential sets')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSets()
  }, [fetchSets])

  // ── Create ──

  const handleCreate = async () => {
    if (!createName.trim()) return
    setIsCreating(true)
    setCreateError(null)
    try {
      const res = await apiClient.fetch('/api/admin/credential-sets', {
        method: 'POST',
        body: JSON.stringify({ name: createName.trim(), description: createDesc.trim() || null }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed to create' }))
        throw new Error(data.detail || 'Failed to create credential set')
      }
      setShowCreate(false)
      setCreateName('')
      setCreateDesc('')
      await fetchSets()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create')
    } finally {
      setIsCreating(false)
    }
  }

  // ── Edit metadata ──

  const openEdit = (cs: CredentialSetSummary) => {
    setEditTarget(cs)
    setEditName(cs.name)
    setEditDesc(cs.description || '')
  }

  const handleSaveEdit = async () => {
    if (!editTarget || !editName.trim()) return
    setIsSavingEdit(true)
    try {
      const res = await apiClient.fetch(`/api/admin/credential-sets/${editTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: editName.trim(), description: editDesc.trim() || null }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed' }))
        throw new Error(data.detail || 'Failed to update')
      }
      setEditTarget(null)
      await fetchSets()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update')
    } finally {
      setIsSavingEdit(false)
    }
  }

  // ── Delete ──

  const handleDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      const res = await apiClient.fetch(`/api/admin/credential-sets/${deleteTarget.id}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Failed to delete')
      setDeleteTarget(null)
      await fetchSets()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete')
    } finally {
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <PageFrame width="list" className="space-y-6">
      <PageHeader
        title="Advanced: credential sets"
        subtitle={
          <>
            Group keys into reusable sets and assign them to users. Keys are
            edited in{' '}
            <Link to="/admin/integrations" className="text-primary hover:underline">
              Integrations
            </Link>.
          </>
        }
        actions={
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create New Set
          </Button>
        }
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {sets.length === 0 ? (
        <div className="text-center py-12 bg-background border border-border rounded-lg">
          <Key className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
          <p className="text-muted-foreground">No credential sets yet.</p>
          <p className="text-sm text-muted-foreground mt-1">Create one to start managing API keys in bulk.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sets.map((cs) => (
            <div
              key={cs.id}
              className="bg-background border border-border rounded-lg p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    {cs.name}
                  </h3>
                  {cs.description && (
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {cs.description}
                    </p>
                  )}
                </div>
                {cs.created_by_name && (
                  <span className="text-xs text-muted-foreground">
                    Created by {cs.created_by_name}
                  </span>
                )}
              </div>

              {/* Service badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {cs.services.length > 0 ? (
                  cs.services.map((svc) => (
                    <Badge key={svc} variant="success">{svc}</Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground italic">No keys configured</span>
                )}
                {cs.origin === 'oauth' && (
                  <Badge variant="secondary">
                    OAuth · {cs.origin_adapter_id ?? 'connected'}
                  </Badge>
                )}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Assigned to {cs.assigned_user_count} user{cs.assigned_user_count !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Button variant="secondary" size="sm" onClick={() => setUsersTarget(cs)}>
                    <Users className="w-4 h-4 mr-1.5" />
                    Manage Users ({cs.assigned_user_count})
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(cs)} title="Edit details">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteTarget(cs)}
                    title="Delete set"
                    className="text-destructive hover:text-destructive/90"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create Set Modal ── */}
      <Modal open={showCreate} onOpenChange={setShowCreate}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Create Credential Set</ModalTitle>
          </ModalHeader>
          <ModalBody>
            {createError && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{createError}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-4">
              <Input
                label="Name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g., Production Keys"
                required
              />
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">Description</label>
                <Textarea
                  value={createDesc}
                  onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="Optional description..."
                  rows={2}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} isLoading={isCreating} disabled={!createName.trim()}>
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Edit Details Modal ── */}
      <Modal open={!!editTarget} onOpenChange={(open) => !open && setEditTarget(null)}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Edit Set Details</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                required
              />
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">Description</label>
                <Textarea
                  value={editDesc}
                  onChange={(e) => setEditDesc(e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setEditTarget(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} isLoading={isSavingEdit} disabled={!editName.trim()}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <Modal open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle>Delete Credential Set</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Alert variant="destructive">
              <AlertDescription>
                <div>
                  <p className="font-medium">This action cannot be undone.</p>
                  <p className="mt-1">
                    Deleting <strong>{deleteTarget?.name}</strong> will remove all its API keys
                    and unassign all {deleteTarget?.assigned_user_count} user{deleteTarget?.assigned_user_count !== 1 ? 's' : ''}.
                    Those users will lose access to any keys they were inheriting from this set.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={isDeleting}>
              Delete Set
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ── Manage Users Sub-Modal ── */}
      {usersTarget && (
        <ManageSetUsersModal
          setId={usersTarget.id}
          setName={usersTarget.name}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setUsersTarget(null)
              fetchSets()
            }
          }}
        />
      )}
    </PageFrame>
  )
}
